import React, { useState, useEffect } from "react";
import { Box } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { getScheduledJob, putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import WorkflowDetail from "modules/analyst/components/WorkflowDetail";

export type JobFormView = "process" | "guidance";

interface WorkflowDetailContainerProps {
  jobName: string;
  setJobName: (value: string) => void;
  selectedJob?: Header;
  /** Fires when the user confirms a name edit — triggers a name-only API save. */
  triggerNameSave?: boolean;
  setTriggerNameSave?: (value: boolean) => void;
  /** Which slice of the job form to show (single instance preserves state across tab switches). */
  jobFormView?: JobFormView;
  setDarwinName: (value: string) => void;
}

const WorkflowDetailContainer: React.FC<WorkflowDetailContainerProps> = ({
  jobName,
  setJobName,
  selectedJob,
  triggerNameSave = false,
  setTriggerNameSave,
  jobFormView = "process",
  setDarwinName,
}) => {
  const [trigger, setTrigger] = useState<string>("");
  const [analysisInstructions, setAnalysisInstructions] = useState<string>("");
  const [reportingInstructions, setReportingInstructions] = useState<string>("");
  const [selectedJobDetails, setSelectedJobDetails] = useState<ScheduledJobProto | undefined>(undefined);

  // Schedule state kept in sync with CronBuilder
  const [isActive, setIsActive] = useState<boolean>(false);
  const [cronExpression, setCronExpression] = useState<string>("");

  const fetchJobDetails = async (selectedJobId: string) => {
    const response = await getScheduledJob(selectedJobId, false);
    if (response) {
      setSelectedJobDetails(response);
      setJobName(response.header?.name || "");
      setTrigger(response.scheduled_analysis?.trigger || "");
      setAnalysisInstructions(response.scheduled_analysis?.analysis_instructions || "");
      setReportingInstructions(response.scheduled_analysis?.reporting_instructions || "");
      setCronExpression(response.cron_expr || "");
      setIsActive(response.is_active || false);
      setDarwinName(response.scheduled_analysis?.darwin_name || "");
    }
  };

  // Name-only save — updates just the header name, preserving all other job fields
  const handleSaveNameOnly = async () => {
    if (!selectedJobDetails?.header?.id) return;
    if (!jobName.trim()) {
      showToast("Workflow name cannot be empty", "", "warning");
      return;
    }
    const job = ScheduledJobProto.create({
      ...selectedJobDetails,
      header: Header.create({
        ...selectedJobDetails.header,
        name: jobName.trim(),
      }),
    });
    const response = await putScheduledJob(job);
    if (response) {
      showToast("Workflow name updated", "", "success");
    } else {
      showToast("Failed to update workflow name", "", "error");
    }
  };

  // Watch for triggerNameSave — fires when user confirms a name edit on an existing job
  useEffect(() => {
    if (triggerNameSave) {
      handleSaveNameOnly();
      setTriggerNameSave?.(false);
    }
  }, [triggerNameSave]);

  // Fetch job details when selectedJob changes; reset to empty when no job selected
  useEffect(() => {
    if (selectedJob?.id) {
      fetchJobDetails(selectedJob.id);
    } else {
      setJobName("");
      setTrigger("");
      setAnalysisInstructions("");
      setReportingInstructions("");
      setCronExpression("");
      setIsActive(false);
      setSelectedJobDetails(undefined);
    }
  }, [selectedJob?.id]);

  return (
    <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column" overflow="hidden">
      <WorkflowDetail
        jobFormView={jobFormView}
        trigger={trigger}
        analysisInstructions={analysisInstructions}
        reportingInstructions={reportingInstructions}
        cronExpression={cronExpression}
        setCronExpression={setCronExpression}
        isActive={isActive}
        setIsActive={setIsActive}
        selectedJobDetails={selectedJobDetails}
      />
    </Box>
  );
};

export default WorkflowDetailContainer;
